import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Users, Trophy, Calendar as CalIcon, Sparkle } from "lucide-react";
import Logo from "../components/Logo";
import SportIcon from "../components/SportIcon";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

const HERO_IMG = "https://images.pexels.com/photos/15612082/pexels-photo-15612082.jpeg";
const PADEL_IMG = "https://images.pexels.com/photos/35248404/pexels-photo-35248404.jpeg";
const TENNIS_IMG = "https://images.unsplash.com/photo-1761927055615-f59ae714385b";
const BASKET_IMG = "https://images.pexels.com/photos/32619170/pexels-photo-32619170.jpeg";
const GOLF_IMG = "https://images.unsplash.com/photo-1708906440951-ea706a9c7423";

export default function Landing() {
  const { t, lang, setLang, languages } = useI18n();
  const { user } = useAuth();
  const [sports, setSports] = useState([]);

  useEffect(() => { api.get("/sports").then((r) => setSports(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Top bar */}
      <div className="pp-glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo"><Logo size={40} /></Link>
          <div className="flex items-center gap-2">
            <select
              data-testid="landing-lang"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="bg-[#131313] border border-white/10 rounded-lg text-sm px-3 py-2 text-white"
            >
              {languages.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
            {user ? (
              <Link to="/dashboard" data-testid="landing-cta-dashboard" className="pp-btn-primary px-4 py-2 rounded-lg text-sm">{t("dashboard")}</Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login" className="pp-btn-secondary px-4 py-2 rounded-lg text-sm">{t("login")}</Link>
                <Link to="/register" data-testid="landing-register" className="pp-btn-primary px-4 py-2 rounded-lg text-sm">{t("register")}</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.55) 60%, rgba(10,10,10,0.85) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="pp-chip mb-6"><Sparkle size={14} className="text-[#00E676]" /> Multi-sport · 9 languages · Global</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight max-w-4xl">
              {t("tagline").split(".").map((s, i) => (
                <span key={`tagline-${i}-${s.slice(0, 4)}`} className={i === 1 ? "pp-grad-text block" : "block"}>{s.trim()}{s ? "." : ""}</span>
              ))}
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-zinc-300 leading-relaxed">{t("hero_sub")}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={user ? "/dashboard" : "/register"} data-testid="hero-play-now" className="pp-btn-primary px-6 py-3 rounded-xl flex items-center gap-2">
                {t("play_now")} <ArrowRight size={18} />
              </Link>
              <Link to="/tournaments" data-testid="hero-explore" className="pp-btn-secondary px-6 py-3 rounded-xl flex items-center gap-2">
                {t("explore_events")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sports strip */}
      <section className="border-y border-white/5 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="overline text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t("sports")}</div>
          <div className="flex flex-wrap gap-3" data-testid="sports-list">
            {sports.map((s) => (
              <div key={s.id} className="pp-chip" data-testid={`sport-chip-${s.slug}`}>
                <SportIcon slug={s.slug} size={14} className="text-[#00E676]" /> {s.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bento features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
          One platform. <span className="pp-grad-text">Every game.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-6 gap-6">
          <FeatureCard data-testid="feature-matches" className="md:col-span-4 row-span-2 min-h-[360px]" img={PADEL_IMG} icon={<Users size={20} />} title={t("matches")} desc={t("play_now_subtitle")} />
          <FeatureCard data-testid="feature-tournaments" className="md:col-span-2" img={TENNIS_IMG} icon={<Trophy size={20} />} title={t("tournaments")} desc="Brackets, registrations, prizes." />
          <FeatureCard data-testid="feature-coaches" className="md:col-span-2" img={GOLF_IMG} icon={<CalIcon size={20} />} title={t("lessons")} desc="Book a coach in minutes." />
          <FeatureCard data-testid="feature-leagues" className="md:col-span-3" img={BASKET_IMG} icon={<Trophy size={20} />} title={t("leagues")} desc="Seasons, standings, ranks." />
          <FeatureCard data-testid="feature-clubs" className="md:col-span-3" icon={<MapPin size={20} />} title={t("clubs")} desc="Discover clubs near you." />
        </div>
      </section>

      {/* CTA strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="pp-card-elevated p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h3 className="font-heading text-3xl lg:text-4xl font-bold">Ready to <span className="pp-grad-text">play</span>?</h3>
            <p className="text-zinc-400 mt-3 max-w-xl">Join Pick2Play and find a match, lesson, tournament or league anywhere in the world — in your language.</p>
          </div>
          <Link to="/register" data-testid="cta-register" className="pp-btn-primary px-6 py-3 rounded-xl flex items-center gap-2">
            {t("register")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-zinc-500 flex flex-col sm:flex-row items-center justify-center gap-3">
        <span>© {new Date().getFullYear()} Pick2Play.</span>
        <span className="hidden sm:inline">·</span>
        <a href="/developers" data-testid="footer-developers" className="hover:text-white transition-colors">Developers</a>
      </footer>
    </div>
  );
}

function FeatureCard({ img, icon, title, desc, className = "", ...rest }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className={`pp-card overflow-hidden relative ${className}`}
      {...rest}
    >
      {img ? (
        <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: `url(${img})` }} />
      ) : null}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.95) 100%)" }} />
      <div className="relative p-6 h-full flex flex-col justify-end">
        <div className="text-[#00E676]">{icon}</div>
        <div className="mt-3 font-heading text-2xl font-bold">{title}</div>
        <div className="text-zinc-400 text-sm mt-1">{desc}</div>
      </div>
    </motion.div>
  );
}
